import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FileUpload from "./FileUpload";

import { readProduct, updateProduct } from "../../../function/product";
import { listCategory } from "../../../function/category";
import { listCategoryService } from "../../../function/categoryservice";
import { listServiceCentersByCategory } from "../../../function/servicecenter";
import DOMPurify from "dompurify";
import "./CreateProduct.css";
import "./UpdateProduct.css"; 

const UpdateProduct = () => {
  const initialstate = {
    title: "",
    description: "",
    categories: [],
    category: "",
    productCategory: "",
    price: "",
    quantity: "",
    images: [],
    points: "",
    employeeDiscount: "",
    isActive: true,
    inactiveReason: "",
  };

  const params = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => ({ ...state }));

  const [values, setValues] = useState(initialstate);
  const [category, setCategory] = useState([]);
  const [categoriesService, setCategoriesService] = useState([]);
  const [selectedSource, setSelectedSource] = useState("category");
  const [loading, setLoading] = useState(false);

  const [serviceCenters, setServiceCenters] = useState([]);
  const [selectedServiceCenters, setSelectedServiceCenters] = useState([]);
  const [showAddServiceCenter, setShowAddServiceCenter] = useState(false);
  const [newServiceCenter, setNewServiceCenter] = useState({
    name: "",
    province: "",
    image: "",
    latitude: "",
    longitude: "",
    mao: "",
  });

  const [productCategories, setProductCategories] = useState([]);
  const [loadingProductCategories, setLoadingProductCategories] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (values.category) {
      listServiceCentersByCategory(values.category._id || values.category)
        .then((res) => setServiceCenters(res.data))
        .catch(() => setServiceCenters([]));
    } else {
      setServiceCenters([]);
    }
  }, [values.category]);

  useEffect(() => {
    if (values.serviceCenter) {
      setSelectedServiceCenters(values.serviceCenter.map((c) => c._id || c));
    }
  }, [values.serviceCenter]);

  useEffect(() => {
    setLoadingProductCategories(true);
    axios
      .get(`${process.env.REACT_APP_API}/product-category?type=${selectedSource}&isActive=true`)
      .then((res) => setProductCategories(res.data))
      .catch((err) => {
        console.log(err);
        setProductCategories([]);
      })
      .finally(() => setLoadingProductCategories(false));
  }, [selectedSource]);

  const loadData = () => {
    readProduct(params.id)
      .then((res) => {
        setValues({ ...values, ...res.data });
        setSelectedSource(res.data.category ? "category" : "categoryService");
      })
      .catch((err) => {
        console.log(err);
      });

    listCategory(user.token)
      .then((res) => setCategory(res.data))
      .catch((err) => console.log(err));

    listCategoryService(user.token)
      .then((res) => setCategoriesService(res.data))
      .catch((err) => console.log(err));
  };

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleSourceChange = (e) => {
    setSelectedSource(e.target.value);
    setValues((prev) => ({ ...prev, productCategory: "" }));
  };

  const handleDescriptionChange = (value) => {
    setValues((prevValues) => ({
      ...prevValues,
      description: value || "",
    }));
  };

  const handleServiceCenterChange = (e) => {
    const options = Array.from(e.target.selectedOptions, (option) => option.value);
    setSelectedServiceCenters(options);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const cleanDescription = DOMPurify.sanitize(values.description);

    updateProduct(user.token, values._id, {
      ...values,
      description: cleanDescription,
      serviceCenter: selectedServiceCenters,
    })
      .then(() => {
        setLoading(false);
        toast.success("อัพเดตสินค้าสำเร็จ");
        navigate("/admin/index");
      })
      .catch((err) => {
        setLoading(false);
        console.error("Update failed:", err.response ? err.response.data : err);
        toast.error("อัพเดตสินค้าไม่สำเร็จ");
      });
  };

  const handleAddServiceCenter = async () => {
    try {
      const payload = {
        name: newServiceCenter.name,
        province: newServiceCenter.province,
        image: newServiceCenter.image,
        location: {
          latitude: Number(newServiceCenter.latitude),
          longitude: Number(newServiceCenter.longitude),
          map: newServiceCenter.map,
        },
        category: [values.category._id || values.category],
        categoryType: selectedSource === "categoryService" ? "categoryservice" : "category",
      };
      const resCreate = await axios.post(`${process.env.REACT_APP_API}/service-centers`, payload);
      if (values.category) {
        const res = await listServiceCentersByCategory(values.category._id || values.category);
        setServiceCenters(res.data);

        if (resCreate.data && resCreate.data._id) {
          setSelectedServiceCenters((prev) => [...prev, resCreate.data._id]);
        }
      }
      setShowAddServiceCenter(false);
      setNewServiceCenter({ name: "", province: "", image: "", latitude: "", longitude: "", map: "" });
      toast.success("เพิ่ม Service Center สำเร็จ");
    } catch (error) {
      console.error("Error adding service center:", error);
      toast.error("เพิ่ม Service Center ไม่สำเร็จ");
    }
  };

  return (
    <div className="up-page">
      <div className="up-header">
        <h2 className="up-title">แก้ไขสินค้า</h2>
        {values.title && <p className="up-subtitle">{values.title}</p>}
      </div>

      <form onSubmit={handleSubmit} className="up-form">
        {/* ===== Section: ข้อมูลพื้นฐาน ===== */}
        <div className="up-section">
          <h4 className="up-section-title">ข้อมูลพื้นฐาน</h4>

          <div className="up-field">
            <label>ชื่อสินค้า</label>
            <input
              className="form-control"
              type="text"
              name="title"
              value={values.title}
              onChange={handleChange}
            />
          </div>

          <div className="up-field">
            <label>รายละเอียดสินค้า</label>
            <ReactQuill value={values.description} onChange={handleDescriptionChange} />
          </div>
        </div>

        {/* ===== Section: ราคา & สต๊อก ===== */}
        <div className="up-section">
          <h4 className="up-section-title">ราคา & สต๊อกสินค้า</h4>

          <div className="up-grid-3">
            <div className="up-field">
              <label>ราคา (บาท)</label>
              <input
                className="form-control"
                type="number"
                name="price"
                value={values.price}
                onChange={handleChange}
              />
            </div>
            <div className="up-field">
              <label>จำนวนคงเหลือ</label>
              <input
                className="form-control"
                type="number"
                name="quantity"
                value={values.quantity}
                onChange={handleChange}
              />
            </div>
            <div className="up-field">
              <label>คะแนนสะสม</label>
              <input
                className="form-control"
                type="number"
                name="points"
                value={values.points}
                onChange={handleChange}
                placeholder="คะแนนสะสม"
              />
            </div>
          </div>

          <div className="up-field up-field-half">
            <label>ส่วนลดพนักงาน (%)</label>
            <input
              className="form-control"
              type="number"
              name="employeeDiscount"
              value={values.employeeDiscount || ""}
              onChange={handleChange}
              placeholder="เช่น 10 (หมายถึงลด 10%)"
            />
          </div>
        </div>

        {/* ===== Section: สถานะการขาย ===== */}
        <div className="up-section">
          <h4 className="up-section-title">สถานะการขาย</h4>

          <div className={`up-status-card ${values.isActive === false ? "up-status-off" : "up-status-on"}`}>
            <div className="up-status-row">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isActiveSwitch"
                  checked={values.isActive !== false}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      isActive: e.target.checked,
                      inactiveReason: e.target.checked ? "" : values.inactiveReason,
                    })
                  }
                />
                <label className="form-check-label up-status-label" htmlFor="isActiveSwitch">
                  {values.isActive !== false ? "เปิดขายปกติ" : "ระงับการขายชั่วคราว"}
                </label>
              </div>
              <span className={`up-status-badge ${values.isActive === false ? "off" : "on"}`}>
                {values.isActive !== false ? "Active" : "Inactive"}
              </span>
            </div>

            {values.isActive === false && (
              <input
                className="form-control mt-2"
                type="text"
                placeholder="เหตุผล (ภายใน ไม่แสดงให้ลูกค้าเห็น) เช่น รอต่อสัญญากับบริษัท"
                value={values.inactiveReason || ""}
                onChange={(e) => setValues({ ...values, inactiveReason: e.target.value })}
              />
            )}
          </div>
        </div>

        {/* ===== Section: หมวดหมู่ ===== */}
        <div className="up-section">
          <h4 className="up-section-title">หมวดหมู่สินค้า</h4>

          <div className="up-grid-2">
            <div className="up-field">
              <label>แหล่งหมวดหมู่</label>
              <select className="form-control" value={selectedSource} onChange={handleSourceChange}>
                <option value="category">Category</option>
                <option value="categoryService">Category Service</option>
              </select>
            </div>

            <div className="up-field">
              <label>
                Category <span className="pc-field-hint">(แบรนด์สินค้า)</span>
              </label>
              <select
                className="form-control"
                name="category"
                value={values.category ? values.category._id : ""}
                onChange={handleChange}
                required
              >
                <option value="">-- กรุณาเลือก --</option>
                {selectedSource === "category" &&
                  category.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                {selectedSource === "categoryService" &&
                  categoriesService.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="pc-field-group">
            <label className="pc-field-label">
              หมวดหมู่สินค้า <span className="pc-field-hint">(แยกจากแบรนด์ด้านบน)</span>
            </label>
            <select
              className="pc-select"
              name="productCategory"
              value={values.productCategory ? values.productCategory._id || values.productCategory : ""}
              onChange={handleChange}
              disabled={loadingProductCategories}
            >
              <option value="">{loadingProductCategories ? "กำลังโหลด..." : "-- ไม่ระบุหมวดหมู่ --"}</option>
              {productCategories.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
            {!loadingProductCategories && productCategories.length === 0 && (
              <small className="pc-field-empty">
                ยังไม่มีหมวดหมู่ในกลุ่ม "{selectedSource === "category" ? "สินค้า" : "บริการ"}" นี้
              </small>
            )}
          </div>
        </div>

        {/* ===== Section: รูปภาพ ===== */}
        <div className="up-section">
          <h4 className="up-section-title">รูปภาพสินค้า</h4>
          <FileUpload loading={loading} setLoading={setLoading} values={values} setValues={setValues} />
        </div>

        {/* ===== Section: Service Center ===== */}
        <div className="up-section">
          <h4 className="up-section-title">Service Center</h4>

          <div className="up-field">
            <select
              className="form-control"
              multiple
              value={selectedServiceCenters}
              onChange={handleServiceCenterChange}
              disabled={!values.category}
              size={5}
            >
              {serviceCenters.map((center) => (
                <option key={center._id} value={center._id}>
                  {center.name} ({center.province})
                </option>
              ))}
            </select>
            <small className="text-muted">เลือก Service Center ได้หลายรายการ (กด Ctrl หรือ Cmd)</small>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 mt-2"
            onClick={() => setShowAddServiceCenter(!showAddServiceCenter)}
          >
            + เพิ่ม Service Center ใหม่
          </button>

          {showAddServiceCenter && (
            <div className="up-inline-card">
              <input
                className="form-control mb-2"
                placeholder="ชื่อ Service Center"
                value={newServiceCenter.name}
                onChange={(e) => setNewServiceCenter({ ...newServiceCenter, name: e.target.value })}
                required
              />
              <input
                className="form-control mb-2"
                placeholder="จังหวัด"
                value={newServiceCenter.province}
                onChange={(e) => setNewServiceCenter({ ...newServiceCenter, province: e.target.value })}
                required
              />
              <input
                className="form-control mb-2"
                placeholder="URL รูปภาพ"
                value={newServiceCenter.image}
                onChange={(e) => setNewServiceCenter({ ...newServiceCenter, image: e.target.value })}
                required
              />
              <div className="up-grid-2">
                <input
                  className="form-control mb-2"
                  placeholder="ละติจูด (Latitude)"
                  type="number"
                  value={newServiceCenter.latitude}
                  onChange={(e) => setNewServiceCenter({ ...newServiceCenter, latitude: e.target.value })}
                  required
                />
                <input
                  className="form-control mb-2"
                  placeholder="ลองจิจูด (Longitude)"
                  type="number"
                  value={newServiceCenter.longitude}
                  onChange={(e) => setNewServiceCenter({ ...newServiceCenter, longitude: e.target.value })}
                  required
                />
              </div>
              <input
                className="form-control mb-2"
                placeholder="Google Map URL"
                onChange={(e) => setNewServiceCenter({ ...newServiceCenter, map: e.target.value })}
              />
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-success" onClick={handleAddServiceCenter}>
                  บันทึก
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddServiceCenter(false)}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===== Submit ===== */}
        <div className="up-submit-bar">
          <button className="btn btn-primary up-submit-btn" disabled={loading}>
            {loading ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateProduct;